import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../apiConfig';
import UpdateProductForm from '../../components/update/update-product-form';
import { useAuth } from "../../context/AuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import Loading from '../../cors/Loading';

export default function ProductUpdatePage() {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [availableVariants, setAvailableVariants] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    if (!token) {
      navigate('/login');
      return;
    }

    const axiosInstance = axios.create({
      baseURL: BASE_URL,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const fetchData = async () => {
      try {
        const results = await Promise.allSettled([
          axiosInstance.get(`/api/products/${id}`),
          axiosInstance.get('/api/categories'),
          axiosInstance.get('/api/getsallers'),
          axiosInstance.get('/api/variants'),
          axiosInstance.get('/api/fournisseurs'),
        ]);

        const [productRes, categoriesRes, clientsRes, variantsRes, fournisseursRes] = results;

        if (productRes.status === 'fulfilled' && productRes.value.data.status && productRes.value.data.data) {
          setProduct(productRes.value.data.data);
        } else {
          throw new Error(productRes.reason?.response?.data?.message || 'Failed to fetch product data.');
        }

        if (categoriesRes.status === 'fulfilled') setCategories(categoriesRes.value.data.data || []);
        if (clientsRes.status === 'fulfilled') setAvailableClients(clientsRes.value.data.data || []);
        if (variantsRes.status === 'fulfilled') setAvailableVariants(variantsRes.value.data || []);
        if (fournisseursRes.status === 'fulfilled') setFournisseurs(fournisseursRes.value.data || []);
      } catch (err) {
        console.error(err);
        setError(err.message || 'An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, token]);

  if (!id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-xl font-bold text-red-700 mb-4">Error</h2>
          <p className="text-gray-700 mb-6">Product ID is missing.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-xl font-bold text-red-700 mb-4">Error Loading Product</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <p className="text-gray-500 text-sm mt-4">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-700 mb-4">Product Not Found</h2>
          <p className="text-gray-600">The product with ID "{id}" could not be loaded or does not exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl max-xl:text-3xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
            Modifier le Product
          </h1>
          <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl mx-auto">
            Modifier les details product.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <UpdateProductForm
            productData={product}
            categories={categories}
            availableClients={availableClients}
            availableVariants={availableVariants}
            Fournisseurs={fournisseurs}
          />
        </div>
      </div>
    </div>
  );
}
